import { EVENT_CATALOGUE } from '../catalogue/event-codes.js';
// The event codes on the wire (Dokless's rules v1 §4.1, fix 9; ADR 0084):
// `events[].code` is one of the normalised codes of the catalogue, never a
// free string. `GET /dokless/v2/event-catalogue` publishes each code with the
// Dokless type it becomes, as Dokless wrote it in its answer of 17/10/2026
// (docs/dokless/risposta-dokless-v1-bozza.md §5): `null` = register only (no
// milestone, no notification to the customer).
// The builder sends a code in Dokless's vocabulary (`doklessWireCode`): its
// Dokless type when it has one (`booked` → `booking_confirmed`,
// `customs_cleared_import` → `customs_cleared`), `ts_loaded`/`ts_discharged`
// as `transshipment` only at a real transshipment (`role: transshipment`), a
// register-only code under its own name. `events[].code` is exactly that set.
/** When a Dokless type applies only under a condition Dokless checks. */
export const DOKLESS_TYPE_CONDITIONS = ['real_transshipment'];
/**
 * Dokless's translation of each code (answer of 17/10, §5; rules §4.1). A code
 * not here is one Dokless has not named yet: register only, `dokless_confirmed: false`.
 */
const DOKLESS_MAPPING = {
    booked: { type: 'booking_confirmed' },
    empty_dispatched: { type: null },
    empty_picked_up: { type: null },
    stuffed: { type: null },
    gate_in: { type: 'gate_in' },
    customs_cleared_export: { type: null },
    loaded_on_vessel: { type: 'loaded_on_vessel' },
    vessel_departed: { type: 'vessel_departed' },
    transshipment: { type: 'transshipment', only_if: 'real_transshipment' },
    ts_discharged: { type: 'transshipment', only_if: 'real_transshipment' },
    ts_loaded: { type: 'transshipment', only_if: 'real_transshipment' },
    vessel_arrived: { type: 'vessel_arrived' },
    discharged_from_vessel: { type: 'discharged_from_vessel' },
    customs_cleared_import: { type: 'customs_cleared' },
    available_for_pickup: { type: null },
    gate_out: { type: 'gate_out' },
    delivered: { type: 'delivered' },
    empty_returned: { type: 'empty_returned' },
    rolled_over: { type: null, signal: 'warning' },
    held: { type: null, signal: 'warning' },
    inspection: { type: null, signal: 'warning' },
    demurrage_started: { type: null },
    detention_started: { type: null },
};
/**
 * The code Plancia sends for a catalogue code (the builder's `doklessEventCode`): the Dokless type, the
 * code itself when register only, the code itself when the type's condition does not hold (a half of a
 * transshipment where no vessel change happened: `role` is not `transshipment`).
 */
export function doklessWireCode(code, role) {
    const mapping = DOKLESS_MAPPING[code];
    if (!mapping?.type)
        return code;
    if (mapping.only_if === 'real_transshipment' && role !== 'transshipment')
        return code;
    return mapping.type;
}
/** Every code that may appear in `events[].code`: exactly what `doklessWireCode` can return. */
export const EVENT_WIRE_CODES = (() => {
    const codes = new Set();
    for (const e of EVENT_CATALOGUE) {
        const mapping = DOKLESS_MAPPING[e.code];
        if (!mapping?.type || mapping.only_if)
            codes.add(e.code);
        if (mapping?.type)
            codes.add(mapping.type);
    }
    if (codes.size === 0)
        throw new Error('empty event catalogue');
    return [...codes];
})();
/** `GET /dokless/v2/event-catalogue`'s items, in catalogue order. */
export function doklessEventCatalogue() {
    return EVENT_CATALOGUE.map((e) => {
        const mapping = DOKLESS_MAPPING[e.code];
        return {
            code: e.code,
            dokless_type: mapping?.type ?? null,
            dokless_type_only_if: mapping?.only_if ?? null,
            dokless_signal: mapping?.signal ?? null,
            dokless_confirmed: mapping !== undefined,
            mode: e.mode,
            phase: e.phase,
            fact_candidate: e.isFactCandidate,
            label: { it: e.labelIt, en: e.labelEn },
            description: e.description,
        };
    });
}
//# sourceMappingURL=event-catalogue.js.map